import App from "@/App";
import ErrorPage from "@/pages/Errors/ErrorPage";
import NoMatch from "@/pages/Errors/NoMatch";
import LoginCheck from "@/pages/auth/login-check/page";
import LoginCode from "@/pages/auth/login/LoginCode";
import Login from "@/pages/auth/login/page";
import Orders from "@/pages/orders/page";
import MenuDetailsPage from "@/pages/menus/detail/page";
import EditMenu from "@/pages/menus/edit/page";
import ListMenus from "@/pages/menus/page";
import DailyDishesPage from "@/pages/daily-dishes/page";
import NoAccess from "@/pages/no-access/page";
import Settings from "@/pages/settings/page";
import Profile from "@/pages/profile/page";
import Dashboard from "@/pages/dashboard";
import HubPage from "@/pages/hub/page";
import CustomersList from "@/pages/customers/CustomersList";
import CustomerDetails from "@/pages/customers/CustomerDetails";
import PosPage from "@/pages/pos/page";
import KitchenPage from "@/pages/kitchen/page";
import RegisterPage from "@/pages/register/page";
import SessionsPage from "@/pages/register/sessions/page";
import SessionDetailPage from "@/pages/register/sessions/[id]/page";
import ReportsPage from "@/pages/reports/page";
import ExpensesPage from "@/pages/expenses/page";
import ReportsPaymentsPage from "@/pages/reports/payments/page";
import ActivityLogsPage from "@/pages/activity-logs/page";
import InventoryPage, {
  InventoryIndexRedirect,
} from "@/pages/inventory/page";
import IngredientsPage from "@/pages/inventory/ingredients/page";
import InventorySuppliersPage from "@/pages/inventory/suppliers/page";
import InventoryPurchaseOrdersPage from "@/pages/inventory/purchase-orders/page";
import InventoryPurchaseOrderNewPage from "@/pages/inventory/purchase-orders/new";
import InventoryPurchaseOrderDetailPage from "@/pages/inventory/purchase-orders/detail";
import InventoryStockCountsPage from "@/pages/inventory/stock-counts/page";
import InventoryStockCountDetailPage from "@/pages/inventory/stock-counts/detail";
import InventoryAdjustmentsPage from "@/pages/inventory/adjustments/page";
import InventoryReportsPage from "@/pages/inventory/reports/page";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import { ORDERS_VIEW_PERMISSIONS, POS_PERMISSIONS } from "@/lib/navigation";
import { Roles } from "@/redux/services/user/user.type";
import { createBrowserRouter, RouteObject } from "react-router-dom";

const withErrorElement = (routes: RouteObject[]): RouteObject[] => {
  return routes.map((route) => {
    if ("children" in route && route.children) {
      return {
        ...route,
        errorElement: <ErrorPage />,
        children: withErrorElement(route.children),
      };
    }
    return {
      ...route,
      errorElement: <ErrorPage />,
    };
  });
};

export const routes = [
  {
    path: "/",
    element: <App />,
  },
  {
    path: "/dashboard",
    element: <Dashboard />,
  },
  {
    path: "/hub",
    element: <HubPage />,
  },
  {
    path: "/menus",
    element: <ListMenus />,
  },
  {
    path: "/menus/daily-dishes",
    element: <DailyDishesPage />,
  },
  {
    path: "/menus/:id/edit",
    element: <EditMenu />,
  },
  {
    path: "/menus/:id",
    element: <MenuDetailsPage />,
  },
  {
    path: "/orders",
    element: (
      // orders.view decides alone (allowedRoles is then ignored); every
      // functional role holds it by default (SFRESTO-184).
      <RoleProtectedPage
        allowedRoles={[
          Roles.manager,
          Roles.owner,
          Roles.cashier,
          Roles.waiter,
          Roles.kitchen,
        ]}
        allowedPermissions={ORDERS_VIEW_PERMISSIONS}
      >
        <Orders />
      </RoleProtectedPage>
    ),
  },
  {
    path: "/customers",
    element: <CustomersList />,
  },
  {
    path: "/customers/:customerId",
    element: <CustomerDetails />,
  },
  {
    path: "/pos",
    element: (
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner, Roles.waiter, Roles.cashier]}
        allowedPermissions={POS_PERMISSIONS}
      >
        <PosPage />
      </RoleProtectedPage>
    ),
  },
  {
    path: "/kitchen",
    element: <KitchenPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/register/sessions",
    element: <SessionsPage />,
  },
  {
    path: "/register/sessions/:id",
    element: <SessionDetailPage />,
  },
  {
    path: "/reports",
    element: <ReportsPage />,
  },
  {
    path: "/reports/payments",
    element: <ReportsPaymentsPage />,
  },
  {
    path: "/activity-logs",
    element: <ActivityLogsPage />,
  },
  {
    path: "/inventory",
    element: (
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["inventory.view", "suppliers.view"]}
      >
        <InventoryPage />
      </RoleProtectedPage>
    ),
    children: [
      { index: true, element: <InventoryIndexRedirect /> },
      { path: "ingredients", element: <IngredientsPage /> },
      { path: "suppliers", element: <InventorySuppliersPage /> },
      { path: "purchase-orders", element: <InventoryPurchaseOrdersPage /> },
      {
        path: "purchase-orders/new",
        element: <InventoryPurchaseOrderNewPage />,
      },
      {
        path: "purchase-orders/:id",
        element: <InventoryPurchaseOrderDetailPage />,
      },
      { path: "stock-counts", element: <InventoryStockCountsPage /> },
      {
        path: "stock-counts/:id",
        element: <InventoryStockCountDetailPage />,
      },
      { path: "adjustments", element: <InventoryAdjustmentsPage /> },
      {
        path: "reports",
        element: (
          <RoleProtectedPage
            allowedRoles={[Roles.manager, Roles.owner]}
            allowedPermissions={["inventory.reports"]}
          >
            <InventoryReportsPage />
          </RoleProtectedPage>
        ),
      },
    ],
  },
  {
    path: "/expenses",
    element: (
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["expenses.view"]}
      >
        <ExpensesPage />
      </RoleProtectedPage>
    ),
  },
  {
    path: "/settings",
    element: <Settings />,
  },
  {
    path: "/profile",
    element: <Profile />,
  },
  {
    path: "auth/login",
    element: <Login />,
  },
  {
    path: "auth/login_code",
    element: <LoginCode />,
  },
  {
    path: "login_link",
    element: <LoginCheck />,
  },
  { path: "no-access", element: <NoAccess /> },
  {
    path: "*",
    element: <NoMatch />,
  },
];

export const router = createBrowserRouter(withErrorElement(routes));
